import {
  Box,
  Drawer,
  Paper,
  useMediaQuery,
  useTheme,
} from '@locus-taxy/encore-mui';
import { GenericErrorPage } from '@locus-taxy/encore-ui-toolkit';
import WorkflowTimelineContainer from './WorkflowTimelineContainer';
import EntityDetailsForm from './EntityDetailsForm';
import type { EntityDetailsBodyProps } from './types';
import { TourDetailsContainer } from './TourDetailsContainer';
import LoadingSpinner from '../LoadingSpinner';
import { ShipmentDetailsPageContainer } from '../../customApps/ShipmentDetailsPageContainer';
import { getTestId } from '@locus-taxy/encore-utils';
import { TestIdAction } from '../../utils/testIdUtils';

export default function EntityDetailsBody({
  clientId,
  entityName,
  entityTitle,
  rootInstanceKey,
  entityData,
  detailsForm,
  isLoading,
  error,
  showTimeline,
  onTimelineClose,
  mfeRemountKey,
}: EntityDetailsBodyProps) {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('lg'));
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const hasToggleControl = typeof showTimeline === 'boolean';
  const resolvedShowTimeline = hasToggleControl ? showTimeline : true;
  const shouldRenderTimeline =
    Boolean(rootInstanceKey) && Boolean(resolvedShowTimeline);
  const useGridLayout = isMdUp || !hasToggleControl;

  const isTourView = entityName === 'b2cDeliveryTour';
  if (isLoading) {
    return (
      <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <LoadingSpinner />
      </Box>
    );
  }

  if (error) {
    return <GenericErrorPage />;
  }

  const resolvedClientId =
    clientId ?? (entityData?.clientId as string | undefined) ?? '';

  const timelineContent =
    rootInstanceKey && shouldRenderTimeline ? (
      <WorkflowTimelineContainer
        clientId={resolvedClientId}
        entityName={entityName}
        rootInstanceKey={rootInstanceKey}
        title={`${entityTitle} Timeline`}
      />
    ) : null;

  return (
    <>
      <Box
        data-testid={getTestId({
          action: TestIdAction.VIEW,
          object: 'details-body',
          entity: entityName,
          page: 'details',
        })}
        sx={{
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          ...(useGridLayout
            ? {
                display: 'grid',
                gridTemplateColumns: timelineContent ? '7fr 3fr' : '1fr',
              }
            : {
                display: 'flex',
                flexDirection: 'column',
              }),
        }}
      >
        <Box sx={{ overflow: 'auto' }}>
          {/* Details section */}
          {isTourView ? (
            <TourDetailsContainer
              entityData={entityData}
              remountKey={mfeRemountKey}
            />
          ) : entityName === 'customerShipment' ? (
            <ShipmentDetailsPageContainer
              clientId={resolvedClientId}
              entityName={entityName}
              entityTitle={entityTitle}
              rootInstanceKey={rootInstanceKey}
              entityData={entityData}
              detailsForm={detailsForm}
              showTimeline={showTimeline}
              remountKey={mfeRemountKey}
            />
          ) : (
            <EntityDetailsForm
              entityName={entityName}
              entityData={entityData}
              detailsForm={detailsForm}
              isLoading={isLoading}
              error={error}
            />
          )}
        </Box>
        {useGridLayout && timelineContent ? (
          <Box borderLeft="1px solid" borderColor="divider" overflow="hidden">
            {timelineContent}
          </Box>
        ) : null}
      </Box>
      {hasToggleControl && !useGridLayout && timelineContent ? (
        <Drawer
          anchor={isXs ? 'bottom' : 'right'}
          open={Boolean(showTimeline)}
          onClose={() => onTimelineClose?.()}
          ModalProps={{ keepMounted: true }}
        >
          <Box
            sx={{
              zIndex: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              p: 2,
              gap: 2,
            }}
          >
            <Paper variant="outlined" sx={{ flex: 1, overflow: 'hidden' }}>
              {timelineContent}
            </Paper>
          </Box>
        </Drawer>
      ) : null}
    </>
  );
}
